import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { Button } from 'antd'
import _ from 'lodash'
import { useNavigate } from 'react-router-dom'
import { useUserProfileStatus } from 'hooks/useUserProfileStatus'
import {
  completeOnboardingPromise,
  getRehabProgramPromise,
  joinSkuProgramsPromise,
  updateUserPromise,
} from 'apis/FitOnCareAPI'
import { ScreenKey } from 'views/Care/util'
import { useI18n, useUnavailableStatus } from 'contexts/AuthContext'
import { logAmplitudeEvent } from 'utils/amplitudeInit'
import { logBrazeEvent } from 'utils/brazeInit'
import { rehabCareFlowFinished } from '../util'
import { showNotification } from 'utils/basic'
import { LoadingContent } from 'components/LoadingDialog/index'
import ContentHeader from 'views/Care/ContentHeader'
import { useApplyNewPatientTag } from 'hooks/useApplyTags'
import { getLocalLanguage } from 'utils/authLocalStorage'
import { UnavailableStatus } from 'views/Care/Unavailable'
import { useAssessmentFlag } from 'contexts/FeatureFlagContext'

const SingleProgramContent = (props) => {
  const {
    sku,
    fitonUserId,
    programId,
    from,
    setCurrentKey,
    themes,
    savedData,
    setSavedData,
  } = props
  const { i18n } = useI18n()
  const [isJoined, setJoined] = useState(false)
  const { getUserProfileStatus } = useUserProfileStatus()
  const [rehabProgram, setRehabProgram] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const { applyNewPatientTag } = useApplyNewPatientTag()
  const { setUnavailableStatus } = useUnavailableStatus()
  const navigate = useNavigate()
  const { showAssessment } = useAssessmentFlag()

  const selectedThemes = useMemo(() => {
    return !_.isEmpty(themes) ? themes : []
  }, [themes])

  const { theme, fitonItemId } = useMemo(() => {
    return {
      theme: selectedThemes?.[0]?.name,
      fitonItemId: selectedThemes?.[0]?.fitonItemId,
    }
  }, [selectedThemes])

  const applyTags = useCallback(async () => {
    const { shouldSkipFlow } = await getUserProfileStatus()
    if (!shouldSkipFlow) {
      applyNewPatientTag()
    }
  }, [applyNewPatientTag, getUserProfileStatus])

  const completeOnboarding = useCallback(async () => {
    const resp = await completeOnboardingPromise({
      userId: fitonUserId,
      theme,
      themeId: fitonItemId,
      programId: rehabProgram?.id,
    })
    if (resp.code === 200) {
      console.log('completeOnboarding success')
      applyTags()
    } else {
      console.log('completeOnboarding failed')
    }
  }, [fitonUserId, theme, fitonItemId, rehabProgram?.id, applyTags])

  const getProgram = useCallback(async () => {
    const data = await getRehabProgramPromise({
      userId: fitonUserId,
      programId,
      subCategoryId: fitonItemId,
    })
    const program = _.first(data?.programs) || {}
    setRehabProgram(program)
    setIsLoading(false)
    if (_.isEmpty(program)) {
      setJoined(true)
      showNotification({ type: 'error', title: i18n('No program found') })
      setUnavailableStatus(UnavailableStatus.NOT_ELIGIBLE_CONGRATULATIONS)
      setCurrentKey(ScreenKey.UNAVAILABLE)
    }
  }, [fitonUserId, programId, fitonItemId])

  const joinProgram = useCallback(async () => {
    if (!rehabProgram?.id) {
      return
    }

    const resp = await joinSkuProgramsPromise({
      userId: fitonUserId,
      sku: sku === 'mca' ? 'msk' : sku,
      subCategoryId: fitonItemId,
      programIds: [rehabProgram.id],
      isMca: sku === 'mca',
    })
    if (resp.code === 200) {
      completeOnboarding()
    } else {
      setUnavailableStatus(UnavailableStatus.NOT_ELIGIBLE_CONGRATULATIONS)
      setCurrentKey(ScreenKey.UNAVAILABLE)
    }
    setJoined(true)
  }, [fitonItemId, fitonUserId, rehabProgram?.id, sku])

  useEffect(() => {
    joinProgram()
  }, [joinProgram])

  useEffect(() => {
    getProgram()
  }, [getProgram])

  const logEvent = useCallback(async () => {
    const { shouldSkipFlow } = await getUserProfileStatus()
    logAmplitudeEvent('Screen View: Onboarding - Signup Success', {
      sku: sku,
      theme,
      ...(shouldSkipFlow ? { flow_skip: 1 } : {}),
      language: getLocalLanguage(),
    })
  }, [getUserProfileStatus, sku, theme])

  useEffect(() => {
    logEvent()
    updateUserPromise({
      skuStatus: {
        sku: sku === 'mca' ? 'msk' : sku,
        status: 2,
      },
    })
    logBrazeEvent(`User Approved - ${sku}`)
  }, [logEvent, sku])

  const onNext = () => {
    if (sku === 'mca') {
      logAmplitudeEvent('Click: Onboarding - Signup Success - Continue', {
        sku,
        destination: 'msk',
      })
      if (showAssessment) {
        navigate(
          `/medicare-access/assessment?fitonUserId=${fitonUserId}&variant=${sku}`
        )
        return
      }
    }
    rehabCareFlowFinished(
      true,
      rehabProgram?.id || programId || 0,
      from,
      fitonUserId,
      sku
    )
  }

  const onBack = (e) => {
    props.setRemoveEventListener(false)
    props.onBack(e)
  }

  return (
    <>
      <ContentHeader {...props} onBack={onBack} />
      <div className='info-content'>
        {isLoading ? (
          <div className='loading-content'>
            <LoadingContent />
          </div>
        ) : (
          <div className='content-container'>
            <img
              className='program-card'
              src={rehabProgram?.coverUrl}
              alt='program-card'
            />
            <div className='title'>
              {i18n(sku === 'mca' ? 'You are in!' : 'You’re all set!')}
            </div>
            <div className='subtitle'>
              {sku === 'mca'
                ? i18n(
                    `You've been added to the {0} program which will help you reduce pain, improve mobility, and build strength.`,
                    <b>{rehabProgram?.name}</b>
                  )
                : i18n(
                    `Your insurance has been confirmed and you've been added to the {0} program`,
                    rehabProgram?.name
                  )}
            </div>
            <Button
              loading={!isJoined}
              className='btn-primary full-width-btn button-start'
              onClick={onNext}
            >
              {i18n('Get Started')}
            </Button>
            <div className='bottom-tips'>
              {sku === 'mca'
                ? i18n(
                    'This program is intended to support your wellness and does not replace advice from healthcare providers. If you experience a medical emergency or severe symptoms, call 911 or seek immediate medical attention.'
                  )
                : i18n(
                    `By clicking ‘Start Program’, you acknowledge you are located in a US state in which FitOn Care is authorized to operate ({0}) and accept responsibility for any co-pay, deductible, coinsurance, or other out-of-pocket costs if the program is not fully covered by your health insurance plan.`,
                    <a
                      href='https://fitoncare.com/am-i-covered'
                      target='_blank'
                      rel='noreferrer'
                      className='gradient-font-1'
                    >
                      {i18n('here')}
                    </a>
                  )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default SingleProgramContent
